import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { listNewsletterRecipients } from "../../../../services/newsletterServices";
import { SubscriberRoleChip, getSubscriberRoleMeta } from "../subscriberRoleUtils";
import { CONTENT_ACCENT, contentFieldSx } from "../../content/contentEditorStyles";

const RECENT_SUBSCRIBERS_LIMIT = 10;
const SEARCH_MIN_LENGTH = 2;

const mergeSubscriberOptions = (items, selected, previousOptions = []) => {
  const map = new Map();
  selected.forEach((item) => {
    const existing = previousOptions.find((option) => option.email === item.email) || item;
    map.set(item.email, existing);
  });
  items.forEach((item) => {
    if (item?.email) {
      map.set(item.email, item);
    }
  });
  return [...map.values()];
};

const SubscriberOptionRow = ({ option, selected }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      width: "100%",
      py: 0.25,
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3 }} noWrap>
        {option.email}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.25 }}>
        {getSubscriberRoleMeta(option.role).label}
      </Typography>
    </Box>
    {selected ? (
      <CheckCircleIcon sx={{ color: "#008245", fontSize: 20, flexShrink: 0 }} aria-hidden="true" />
    ) : (
      <SubscriberRoleChip role={option.role} />
    )}
  </Box>
);

SubscriberOptionRow.propTypes = {
  option: PropTypes.shape({
    email: PropTypes.string.isRequired,
    role: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
};

const NewsletterSubscriberSelect = ({ value, onChange, onError }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const loadRecentSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNewsletterRecipients({});
      const recent = (data.subscribers || []).slice(0, RECENT_SUBSCRIBERS_LIMIT);
      setOptions((previous) => mergeSubscriberOptions(recent, value, previous));
    } catch (error) {
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  }, [onError, value]);

  useEffect(() => {
    loadRecentSubscribers();
  }, [loadRecentSubscribers]);

  useEffect(() => {
    const query = inputValue.trim();
    if (query.length < SEARCH_MIN_LENGTH) {
      return undefined;
    }
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await listNewsletterRecipients({ searchFieldValue: query });
        setOptions((previous) => mergeSubscriberOptions(data.subscribers || [], value, previous));
      } catch (error) {
        onError?.(error.message);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [inputValue, onError, value]);

  const selectedOptions = useMemo(
    () => value.map((item) => optionsRef.current.find((option) => option.email === item.email) || item),
    [value]
  );

  const removeSubscriber = (email) => {
    onChange(value.filter((item) => item.email !== email));
  };

  return (
    <Box sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        filterSelectedOptions={false}
        value={selectedOptions}
        options={options}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, nextValue, reason) => {
          setInputValue(nextValue);
          if (reason === "clear" || nextValue.trim().length < SEARCH_MIN_LENGTH) {
            loadRecentSubscribers();
          }
        }}
        onChange={(_, values) => onChange(values)}
        getOptionLabel={(option) => option.email || ""}
        isOptionEqualToValue={(option, item) => option.email === item.email}
        noOptionsText="Nessun iscritto trovato"
        renderTags={() => null}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{
                "&.MuiAutocomplete-option[aria-selected='true']": {
                  backgroundColor: "transparent !important",
                },
                "&.MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
                  backgroundColor: "rgba(11, 61, 135, 0.08) !important",
                },
              }}
            >
              <SubscriberOptionRow option={option} selected={selected} />
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label="Cerca iscritti" placeholder="Digita almeno 2 caratteri" sx={contentFieldSx} />
        )}
      />

      {selectedOptions.length ? (
        <Box sx={{ mt: 1.5, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#64748b", mb: 0.75 }}>
            Iscritti selezionati ({selectedOptions.length})
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              maxHeight: 120,
              overflow: "auto",
              pr: 0,
            }}
          >
            {selectedOptions.map((subscriber) => (
              <Chip
                key={subscriber.email}
                label={subscriber.email}
                size="small"
                onDelete={() => removeSubscriber(subscriber.email)}
                sx={{
                  maxWidth: "100%",
                  bgcolor: "#eef2ff",
                  color: CONTENT_ACCENT,
                  fontWeight: 600,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

NewsletterSubscriberSelect.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      role: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

export default NewsletterSubscriberSelect;
